import type { ThemeObject } from "@circle-react/types/Theme";
import { colord } from "colord";

import type { VariableOptions } from "./themes";
import { APPEARANCE, THEME_MAP, VARIABLE_MAP } from "./themes";

export interface UpdateThemeProps {
  documentElement?: HTMLElement;
  selector?: string;
  theme: ThemeObject;
}

const brandCssVarName = "--brand";

export const updateTheme = ({
  theme,
  documentElement = window.document.documentElement,
  selector = "",
}: // eslint-disable-next-line sonarjs/cognitive-complexity -- Provisory until having brand text color customizable
UpdateThemeProps) => {
  const {
    current_appearance: currentAppearance = APPEARANCE.LIGHT,
    custom_colors: customColors = {},
    current_theme: currentTheme = "default",
    brand_color: brandColor = {
      light: "",
      dark: "",
    },
    brand_text_color: brandTextColor = {
      light: "",
      dark: "",
    },
  }: ThemeObject = theme;

  const hasBrandColor = brandColor?.light && brandColor?.dark;
  const hasBrandTextColor = brandTextColor?.light && brandTextColor?.dark;

  // eslint-disable-next-line sonarjs/no-dead-store -- Disabled for plugin version update. Fix when possible.
  let themeVariables = THEME_MAP.default.light;

  if (currentTheme !== "custom") {
    themeVariables =
      THEME_MAP?.[currentTheme]?.[currentAppearance] ||
      THEME_MAP.default?.[currentAppearance] ||
      THEME_MAP.default.light;
  } else {
    themeVariables =
      THEME_MAP.default?.[currentAppearance] || THEME_MAP.default.light;
  }

  const defaultThemeVariables =
    THEME_MAP.default[currentAppearance] || THEME_MAP.default.light;

  const cssVariables = new Map<string, string>();

  Object.entries(themeVariables)
    .filter(([key]: [string, string]) => key !== brandCssVarName)
    .forEach(([key, value]: [string, string]) => {
      cssVariables.set(key, value);
    });

  if (currentTheme === "custom" && customColors[currentAppearance]) {
    Object.entries(customColors[currentAppearance]).forEach(([key, value]) => {
      const cssVarKey = VARIABLE_MAP[key as VariableOptions];
      if (value) {
        cssVariables.set(cssVarKey, String(value));
      } else if (
        (defaultThemeVariables as unknown as Record<string, string>)[cssVarKey]
      ) {
        // TODO: Find a solution for this type with compass + circle web interfaces
        cssVariables.set(
          cssVarKey,
          String(
            (defaultThemeVariables as unknown as Record<string, string>)[
              cssVarKey
            ],
          ),
        );
      }
    });
  }

  if (hasBrandColor) {
    const color = brandColor[currentAppearance];
    const parsedColor = colord(color);
    const lighterColor = parsedColor.lighten(0.1).toHex();
    const darkerColor = parsedColor.darken(0.1).toHex();
    const hoverColor = parsedColor.isDark() ? lighterColor : darkerColor;

    // General theme variables
    cssVariables.set(brandCssVarName, color);
    cssVariables.set("--brand-button-hover", hoverColor);
    cssVariables.set("--brand-light", lighterColor);
    cssVariables.set("--brand-dark", darkerColor);

    // Compass theme variables
    cssVariables.set("--compass-branded-button-bg", brandColor.light);
    cssVariables.set("--compass-branded-button-bg-dark", brandColor.dark);
    cssVariables.set("--compass-branded-badge-bg", brandColor.light);
    cssVariables.set("--compass-branded-badge-bg-dark", brandColor.dark);
    cssVariables.set("--compass-branded-badge-border", brandColor.light);
    cssVariables.set("--compass-branded-badge-border-dark", brandColor.dark);
    cssVariables.set("--compass-branded-icon", brandColor.light);
    cssVariables.set("--compass-branded-icon-dark", brandColor.dark);
    cssVariables.set("--compass-branded-link-text", brandColor.light);
    cssVariables.set("--compass-branded-link-text-dark", brandColor.dark);
  }

  if (hasBrandTextColor) {
    // General theme variables
    cssVariables.set("--brand-button-text", brandTextColor[currentAppearance]);

    // Compass theme variables
    cssVariables.set("--compass-branded-button-text", brandTextColor.light);
    cssVariables.set("--compass-branded-button-text-dark", brandTextColor.dark);
    cssVariables.set("--compass-branded-badge-text", brandTextColor.light);
    cssVariables.set("--compass-branded-badge-text-dark", brandTextColor.dark);
  }

  let element = documentElement;

  if (selector) {
    try {
      const selected: HTMLElement | null = document.querySelector(selector);
      if (selected) {
        element = selected;
      }
    } catch (error) {
      console.error(error);
      element = documentElement;
    }
  }

  cssVariables.forEach((value, key) => {
    element.style.setProperty(key, value);
  });

  if (currentAppearance === APPEARANCE.DARK) {
    element.classList.add("dark");
  } else {
    element.classList.remove("dark");
  }
};
